import { IManagedFarmTask } from '../../interfaces/ManagedFarm.interfaces';

const DB_NAME = 'ManagedFarmDB';
const DB_VERSION = 1;
const STORE_NAME = 'tasks';

// IndexedDB 초기화
const initDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      reject(request.error);
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // 기존 스토어가 있으면 삭제
      if (db.objectStoreNames.contains(STORE_NAME)) {
        db.deleteObjectStore(STORE_NAME);
      }

      // 새로운 스토어 생성
      const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });

      // 인덱스 생성 (선택사항)
      store.createIndex('startDate', 'startDate', { unique: false });
      store.createIndex('status', 'status', { unique: false });
    };
  });
};

// 태스크 리스트 가져오기
export const getTaskListFromIndexedDB = async (): Promise<IManagedFarmTask[]> => {
  try {
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);

    return new Promise((resolve, reject) => {
      const request = store.getAll();

      request.onerror = () => {
        reject(request.error);
      };

      request.onsuccess = () => {
        resolve(request.result || []);
      };
    });
  } catch (error) {
    console.error('IndexedDB에서 태스크 리스트 가져오기 실패:', error);
    return [];
  }
};

// 태스크 리스트 저장하기
export const saveTaskListToIndexedDB = async (taskList: IManagedFarmTask[]): Promise<void> => {
  try {
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    // 기존 모든 데이터 삭제
    await new Promise<void>((resolve, reject) => {
      const clearRequest = store.clear();
      clearRequest.onerror = () => reject(clearRequest.error);
      clearRequest.onsuccess = () => resolve();
    });

    // 새로운 데이터 저장
    await Promise.all(
      taskList.map(
        (task) =>
          new Promise<void>((resolve, reject) => {
            const request = store.add(task);
            request.onerror = () => reject(request.error);
            request.onsuccess = () => resolve();
          }),
      ),
    );
  } catch (error) {
    console.error('IndexedDB에 태스크 리스트 저장 실패:', error);
    throw error;
  }
};

// 개별 태스크 추가
export const addTaskToIndexedDB = async (task: IManagedFarmTask): Promise<void> => {
  try {
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    return new Promise((resolve, reject) => {
      const request = store.add(task);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  } catch (error) {
    console.error('IndexedDB에 태스크 추가 실패:', error);
    throw error;
  }
};

// 태스크 업데이트
export const updateTaskInIndexedDB = async (task: IManagedFarmTask): Promise<void> => {
  try {
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    return new Promise((resolve, reject) => {
      const request = store.put(task);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  } catch (error) {
    console.error('IndexedDB에서 태스크 업데이트 실패:', error);
    throw error;
  }
};

// 태스크 삭제
export const deleteTaskFromIndexedDB = async (taskId: string): Promise<void> => {
  try {
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    return new Promise((resolve, reject) => {
      const request = store.delete(taskId);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  } catch (error) {
    console.error('IndexedDB에서 태스크 삭제 실패:', error);
    throw error;
  }
};

// localStorage에서 IndexedDB로 데이터 마이그레이션
export const migrateFromLocalStorage = async (): Promise<void> => {
  try {
    const localStorageData = localStorage.getItem('managedFarmTaskList');
    if (localStorageData) {
      const taskList: IManagedFarmTask[] = JSON.parse(localStorageData);
      await saveTaskListToIndexedDB(taskList);

      // 마이그레이션 완료 후 localStorage 데이터 삭제
      localStorage.removeItem('managedFarmTaskList');
    }
  } catch (error) {
    console.error('❌ 데이터 마이그레이션 실패:', error);
  }
};
